/**
 * BrandHeroImage — premium hero rendering for the /login + /signup right rail
 * (and the Branding-tab live previews, so they match pixel-for-pixel).
 *
 * Behaviour:
 *   • Blurred backdrop: a scaled, blurred copy of the image fills the panel
 *     edge-to-edge so there are never empty solid bars.
 *   • Sharp foreground: the full image is shown with object-contain (never
 *     cropped / pixelated) centered on top of the backdrop.
 *   • Auto light/dark tone: the image's edge pixels are sampled to pick a
 *     matching base background + scrim so the framing always blends. Falls
 *     back to a neutral tone when the image can't be read (CORS / not loaded).
 */
import React, { useEffect, useState } from "react";

// Sample the outer-edge pixels of the image and decide a 'light' | 'dark' tone.
function useEdgeTone(src) {
    const [tone, setTone] = useState(null); // 'light' | 'dark' | null
    useEffect(() => {
        if (!src) { setTone(null); return; }
        let cancelled = false;
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            try {
                const w = 32, h = 32;
                const c = document.createElement("canvas");
                c.width = w; c.height = h;
                const ctx = c.getContext("2d", { willReadFrequently: true });
                ctx.drawImage(img, 0, 0, w, h);
                const { data } = ctx.getImageData(0, 0, w, h);
                let sum = 0, n = 0;
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        // Only the outer 4px ring (the "bars" region).
                        if (x > 3 && x < w - 4 && y > 3 && y < h - 4) continue;
                        const i = (y * w + x) * 4;
                        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
                        n++;
                    }
                }
                const lum = n ? sum / n : 128;
                if (!cancelled) setTone(lum >= 140 ? "light" : "dark");
            } catch {
                if (!cancelled) setTone(null); // CORS-tainted → neutral fallback
            }
        };
        img.onerror = () => { if (!cancelled) setTone(null); };
        img.src = src;
        return () => { cancelled = true; };
    }, [src]);
    return tone;
}

export function BrandHeroImage({ src, testId, blur = "blur-2xl" }) {
    const tone = useEdgeTone(src);
    // Fade the image in once it has fully loaded (no half-painted image,
    // no tone/background jump). Resets whenever the src changes.
    const [loaded, setLoaded] = useState(false);
    useEffect(() => { setLoaded(false); }, [src]);
    const baseBg = !loaded ? "bg-muted"
        : tone === "light" ? "bg-neutral-100" : tone === "dark" ? "bg-neutral-900" : "bg-muted";
    const scrim = tone === "light" ? "bg-white/25" : "bg-black/25";
    if (!src) return null;
    const fade = `transition-opacity duration-300 ease-out ${loaded ? "" : "opacity-0"}`;
    return (
        <div className={`absolute inset-0 overflow-hidden transition-colors duration-300 ${baseBg}`}
             data-brand-hero-tone={tone || "neutral"}
             data-brand-hero-loaded={loaded ? "true" : "false"}>
            {/* Blurred backdrop copy — fills the panel edge-to-edge */}
            <img
                src={src}
                alt=""
                aria-hidden="true"
                className={`absolute inset-0 w-full h-full object-cover scale-110 ${blur} ${loaded ? "opacity-60" : "opacity-0"} transition-opacity duration-300 ease-out`}
            />
            {/* Adaptive scrim to gently separate the sharp image from the wash */}
            <div className={`absolute inset-0 ${scrim} ${fade}`} />
            {/* Sharp, fully-visible image (never cropped) */}
            <img
                src={src}
                alt=""
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
                ref={(el) => { if (el && el.complete && el.naturalWidth > 0 && !loaded) setLoaded(true); }}
                className={`relative w-full h-full object-contain object-center ${fade}`}
                data-testid={testId}
            />
        </div>
    );
}

export default BrandHeroImage;
